/**
 * Polls /api/notifications/reminders every 60 seconds.
 * Returns bookings ending within the next 30 minutes.
 * Only active for admin/manager roles.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import API from '../api';
import { useAuth } from './useAuth';

export function useReminders() {
    const { isAdmin, isManager } = useAuth();
    const [reminders, setReminders] = useState([]);
    const [renewals, setRenewals] = useState([]);
    const [notifications, setNotifications] = useState([]);
    const [newCount, setNewCount] = useState(0);
    const seenIds = useRef(new Set());
    const initialized = useRef(false);

    const fetchReminders = useCallback(async () => {
        try {
            const requests = [API.get('/notifications')];
            if (isAdmin || isManager) {
                requests.unshift(API.get('/notifications/reminders?minutes=30'), API.get('/notifications/renewals'));
            }
            const responses = await Promise.all(requests);
            const notificationData = responses.pop().data;
            const renewalData = isAdmin || isManager ? responses.pop().data : [];
            const data = isAdmin || isManager ? responses.pop().data : [];
            setReminders(data);
            setRenewals(renewalData);
            setNotifications(notificationData.notifications || []);
            const items = [
                ...data.map(r => ({ id: `booking-reminder-${r.id}` })),
                ...renewalData.map(r => ({ id: `renewal-${r.id}` })),
                ...(notificationData.notifications || []).map(item => ({ id: `notification-${item.id}` })),
            ];
            if (initialized.current) {
                const fresh = items.filter(item => !seenIds.current.has(item.id));
                if (fresh.length > 0) setNewCount(previous => previous + fresh.length);
            }
            items.forEach(item => seenIds.current.add(item.id));
            initialized.current = true;
        } catch {
            // Silently fail — don't break the UI
        }
    }, [isAdmin, isManager]);

    useEffect(() => {
        fetchReminders();
        const interval = setInterval(fetchReminders, 30000);
        return () => clearInterval(interval);
    }, [fetchReminders]);

    const clearNew = () => setNewCount(0);

    return { reminders, renewals, notifications, newCount, clearNew, refetch: fetchReminders };
}
